/**
 * QuestLabTabs.tsx — the Lab Presets and Lab Codes tabs of Admin >
 * Integrations > Quest (legacy admin_quest_lab_list.php, "Quest Diagnostics").
 *
 * Lab Presets: the practice's shared order presets, A–Z as the order screens
 * list them (no drag to reorder since 2026-10-07), with Show deleted / Restore. Add and
 * edit build a preset from the Quest catalog, as legacy opened its order form
 * in admin mode: a title and the tests. Anything else a preset holds (notes,
 * diagnoses, insurance) is kept as stored.
 *
 * Lab Codes: the practice's own Quest test codes (Test Code, Description,
 * Specimen Type, Specimen State), and a read-only lookup of Quest's catalog.
 *
 * On a Locked page (useAdminPageLevel, plan 2026-10-10-001 U7: R8, R18)
 * nothing changes and nothing says why: no Actions column or row icons, and
 * no empty-list hint inviting an add. A preset or a code still opens, as a
 * viewer (AdminFormModal: fields read-only, a single Close); a preset shows
 * its tests with no test search or remove. Search, Show deleted and the
 * Quest code lookup keep working.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { compareTitles } from '@/components/admin/categoryBoardState';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { parseQuestHl7Order } from '@/pages/patients/labs/questHl7';
import { getErrorMessage } from '@/services/apiClient';
import { getQuestPresetContent, searchQuestTests, type QuestOrderTest, type QuestTestCode } from '@/services/labs.service';
import {
  NO_EXTRAS,
  PRESET_TITLE_MAX,
  QUEST_CODE_MAX,
  QUEST_DESCRIPTION_MAX,
  SPECIMEN_STATES,
  SPECIMEN_TYPES,
  createQuestCode,
  deleteQuestCode,
  deleteQuestPreset,
  listQuestCodes,
  listQuestPresetsAdmin,
  renameQuestPreset,
  restoreQuestPreset,
  saveQuestPresetTests,
  stateLabel,
  updateQuestCode,
  type PresetExtras,
  type QuestCustomCode,
  type QuestPresetRow,
} from '@/services/questLabAdmin.service';
import { toast } from '@/store/toastStore';

const today = () => new Date().toISOString().slice(0, 10);
const presetName = (p: QuestPresetRow) => p.title || `Preset #${p.id}`;

/** Catalog search results for a query (2+ characters), debounced. */
function useQuestTestSearch(query: string) {
  const [results, setResults] = useState<QuestTestCode[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchQuestTests(q)
        .then((rows) => !cancelled && setResults(rows ?? []))
        .catch(() => !cancelled && setResults([]))
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);
  return { results, loading };
}

// ════════════════════════════════════════════════════════════════════════════
// Lab Presets
// ════════════════════════════════════════════════════════════════════════════

const PRESET_COLUMNS: AdminListColumn[] = [{ key: 'title', header: 'Title' }, ACTIONS_COLUMN];

/** A Locked page has no row actions, so no Actions column. */
const withoutActions = (columns: AdminListColumn[]) => columns.filter((c) => c !== ACTIONS_COLUMN);

export function QuestPresetsTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [presets, setPresets] = useState<QuestPresetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<QuestPresetRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<QuestPresetRow | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setPresets(await listQuestPresetsAdmin());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load lab presets'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (showCreate) setEditing('new');
  }, [showCreate]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      presets
        .filter((p) => (showDeleted || p.active) && (query === '' || p.title.toLowerCase().includes(query)))
        .sort((a, b) => compareTitles(presetName(a), presetName(b)) || a.id - b.id),
    [presets, showDeleted, query],
  );

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteQuestPreset(deleting.id);
      toast.success('Lab preset deleted');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The lab preset could not be deleted.'));
    }
  }

  async function handleRestore(p: QuestPresetRow) {
    try {
      await restoreQuestPreset(p.id);
      toast.success('Lab preset restored');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The lab preset could not be restored.'));
    }
  }

  return (
    <div className="px-6 py-4">
      <AdminListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search lab presets..."
        searchLabel="Search lab presets"
        showDeleted={showDeleted}
        onShowDeletedChange={setShowDeleted}
        showDeletedId="quest-presets-show-deleted"
      />
      <p className="mb-3 text-xs text-slate-500">
        Shared presets, in the order the Quest order form lists them. Presets staff keep for themselves are not shown here.
      </p>
      {loading ? (
        <div className="relative h-32">
          <SpinnerOverlay label="Loading lab presets…" />
        </div>
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No lab presets found' : 'No lab presets yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a lab preset to get started.'}
        />
      ) : (
        <AdminListTable columns={locked ? withoutActions(PRESET_COLUMNS) : PRESET_COLUMNS} aria-label="Lab presets">
          {rows.map((p) => {
            const deleted = !p.active;
            const name = presetName(p);
            return (
              <tr
                key={p.id}
                data-row-id={p.id}
                className={adminRowClass({ deleted, clickable: !deleted })}
                onClick={deleted ? undefined : () => setEditing(p)}
              >
                <td className={ADMIN_TD_CLASS}>
                  <AdminRowName deleted={deleted} nameTestId="quest-preset-name">
                    {name}
                  </AdminRowName>
                </td>
                {!locked && (
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowActions>
                      {deleted ? (
                        <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(p)} />
                      ) : (
                        <>
                          <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(p)} />
                          <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(p)} />
                        </>
                      )}
                    </AdminRowActions>
                  </td>
                )}
              </tr>
            );
          })}
        </AdminListTable>
      )}

      {editing && (
        <QuestPresetModal
          key={editing === 'new' ? 'new' : editing.id}
          preset={editing === 'new' ? null : editing}
          takenTitles={presets.filter((p) => editing === 'new' || p.id !== editing.id).map((p) => p.title.trim().toLowerCase())}
          onClose={() => {
            setEditing(null);
            onCreated();
          }}
          onSaved={() => {
            setEditing(null);
            onCreated();
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete lab preset "${presetName(deleting)}"?` : ''}
        message="It leaves the Quest order form's preset list. You can restore it from Show deleted."
        confirmLabel="Delete lab preset"
        variant="danger"
      />
    </div>
  );
}

function QuestPresetModal({
  preset,
  takenTitles,
  onClose,
  onSaved,
}: {
  preset: QuestPresetRow | null;
  /** Every other shared preset's title, lowercased (deleted ones too: the order core finds presets by title). */
  takenTitles: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = preset === null;
  // Locked: a viewer (AdminFormModal) — no test search or remove, and nothing saves.
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(() => ({ title: preset?.title ?? '', tests: [] as QuestOrderTest[] }), { tracking: !isNew });
  const [extras, setExtras] = useState<PresetExtras>(NO_EXTRAS);
  const [loadingTests, setLoadingTests] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const { results, loading: searching } = useQuestTestSearch(locked ? '' : search);

  useEffect(() => {
    if (!preset) return;
    let cancelled = false;
    getQuestPresetContent('preset', preset.id)
      .then((content) => {
        if (cancelled) return;
        const parsed = parseQuestHl7Order(content.fullOrderContent, 'preset', today());
        const tests = parsed.tests.map((x) => ({ testCode: x.testCode, description: x.description, collectionDate: x.collectionDate, obrNotes: x.obrNotes }));
        t.reset({ title: preset.title, tests });
        setExtras({
          facility: parsed.facility ?? '',
          provider: parsed.provider ?? '',
          pidNotes: parsed.pidNotes,
          insurance: parsed.insurance,
          diagnoses: parsed.diagnoses.map((d) => d.code),
        });
      })
      .catch((err) => {
        if (!cancelled) toast.error(getErrorMessage(err, "This preset's tests could not be loaded."));
      })
      .finally(() => !cancelled && setLoadingTests(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.id]);

  const title = t.form.title.trim();
  const errors = [
    ...(title === '' ? ['Title is required'] : []),
    ...(title !== '' && takenTitles.includes(title.toLowerCase()) ? ['Another lab preset already has this title'] : []),
    ...(!loadingTests && t.form.tests.length === 0 ? ['Add at least one test'] : []),
  ];
  const testsChanged = t.changed.has('tests');

  function addTest(code: QuestTestCode) {
    if (t.form.tests.some((x) => x.testCode === code.testCode)) return;
    t.patch({ tests: [...t.form.tests, { testCode: code.testCode, description: code.description, collectionDate: today(), obrNotes: [] }] });
    setSearch('');
  }

  async function save() {
    if (errors.length > 0 || locked) return;
    setSaving(true);
    try {
      // A rename first: the core then finds the preset under its new title.
      if (preset && title !== preset.title) await renameQuestPreset(preset.id, title);
      if (isNew || testsChanged) await saveQuestPresetTests(title, t.form.tests, extras);
      toast.success(isNew ? 'Lab preset created' : 'Lab preset updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The lab preset could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Lab Preset' : 'Edit Lab Preset'}
      description="A shared preset: everyone ordering Quest labs can load it."
      isNew={isNew}
      dirty={isNew ? errors.length === 0 : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="lg"
    >
      <DialogField
        label="Title"
        htmlFor="quest-preset-title"
        required
        state={errors.length > 0 && (title === '' || takenTitles.includes(title.toLowerCase())) ? 'error' : 'none'}
        badge={t.badge('title', 'Title')}
      >
        <Input id="quest-preset-title" value={t.form.title} maxLength={PRESET_TITLE_MAX} onChange={(e) => t.patch({ title: e.target.value })} />
      </DialogField>

      <DialogField label={`Tests (${t.form.tests.length})`} badge={t.badge('tests', 'Tests')}>
        {loadingTests ? (
          <p className="text-sm text-slate-500">Loading tests…</p>
        ) : (
          <div className="space-y-2">
            {!locked && (
              <Input
                aria-label="Search Quest tests"
                placeholder="Search by test code or description…"
                leftAddon={<Search className="h-4 w-4" />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
            {!locked && search.trim().length >= 2 && (
              <ul className="max-h-48 overflow-y-auto rounded border border-slate-200 text-sm" data-testid="quest-test-results">
                {searching && <li className="px-3 py-1.5 text-slate-500">Searching…</li>}
                {!searching && results.length === 0 && <li className="px-3 py-1.5 text-slate-500">No tests found</li>}
                {!searching &&
                  results.map((r) => {
                    const added = t.form.tests.some((x) => x.testCode === r.testCode);
                    return (
                      <li key={r.testCode}>
                        <button type="button" disabled={added} className="w-full px-3 py-1.5 text-left hover:bg-slate-100 disabled:text-slate-400" onClick={() => addTest(r)}>
                          {r.label}
                          {added ? ' (added)' : ''}
                        </button>
                      </li>
                    );
                  })}
              </ul>
            )}
            {t.form.tests.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded border border-slate-200" data-testid="quest-preset-tests">
                {t.form.tests.map((x) => (
                  <li key={x.testCode} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    <span className="font-mono">{x.testCode}</span>
                    <span className="min-w-0 flex-1 truncate text-slate-600">{x.description}</span>
                    {!locked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${x.testCode}`}
                        leftIcon={<X className="h-4 w-4" />}
                        onClick={() => t.patch({ tests: t.form.tests.filter((y) => y.testCode !== x.testCode) })}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogField>
    </AdminFormModal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Lab Codes
// ════════════════════════════════════════════════════════════════════════════

const CODE_COLUMNS: AdminListColumn[] = [
  { key: 'code', header: 'Test Code' },
  { key: 'description', header: 'Description' },
  { key: 'specimenType', header: 'Specimen Type' },
  { key: 'state', header: 'Specimen State' },
  ACTIONS_COLUMN,
];

export function QuestCodesTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [codes, setCodes] = useState<QuestCustomCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<QuestCustomCode | 'new' | null>(null);
  const [deleting, setDeleting] = useState<QuestCustomCode | null>(null);
  const [lookup, setLookup] = useState('');
  const { results, loading: searching } = useQuestTestSearch(lookup);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setCodes(await listQuestCodes());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load lab codes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (showCreate) setEditing('new');
  }, [showCreate]);

  const query = search.trim().toLowerCase();
  const rows = codes.filter((c) => query === '' || c.code.toLowerCase().includes(query) || c.description.toLowerCase().includes(query));
  const customCodes = new Set(codes.map((c) => c.code));

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteQuestCode(deleting.code);
      toast.success('Lab code deleted');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The lab code could not be deleted.'));
    }
  }

  return (
    <div className="px-6 py-4">
      <AdminListToolbar search={search} onSearchChange={setSearch} searchPlaceholder="Search your lab codes..." searchLabel="Search lab codes" />
      <p className="mb-3 text-xs text-slate-500">Your practice's own test codes. Staff find them in the Quest order form's test search, with Quest's.</p>
      {loading ? (
        <div className="relative h-32">
          <SpinnerOverlay label="Loading lab codes…" />
        </div>
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No lab codes found' : 'No lab codes yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a lab code to order a test that is not in Quest’s catalog.'}
        />
      ) : (
        <AdminListTable columns={locked ? withoutActions(CODE_COLUMNS) : CODE_COLUMNS} aria-label="Lab codes">
          {rows.map((c) => (
            <tr key={c.code} className={adminRowClass({ deleted: false, clickable: true })} onClick={() => setEditing(c)}>
              <td className={`${ADMIN_TD_CLASS} font-mono`}>{c.code}</td>
              <td className={ADMIN_TD_CLASS}>{c.description}</td>
              <td className={ADMIN_TD_CLASS}>{c.specimenType || '—'}</td>
              <td className={ADMIN_TD_CLASS}>{c.state ? stateLabel(c.state) : '—'}</td>
              {!locked && (
                <td className={ADMIN_TD_CLASS}>
                  <AdminRowActions>
                    <AdminRowAction kind="edit" label={`Edit ${c.code}`} onClick={() => setEditing(c)} />
                    <AdminRowAction kind="delete" label={`Delete ${c.code}`} onClick={() => setDeleting(c)} />
                  </AdminRowActions>
                </td>
              )}
            </tr>
          ))}
        </AdminListTable>
      )}

      <div className="mt-8">
        <h3 className="text-sm font-semibold text-slate-900">Look up a Quest code</h3>
        {/* Locked never says "read only" (R18), even of Quest's catalog. */}
        <p className="mb-2 text-xs text-slate-500">{locked ? 'Quest’s catalog and your own codes.' : 'Quest’s catalog (read only) and your own codes.'}</p>
        <Input
          aria-label="Look up a Quest code"
          placeholder="Test code or description…"
          leftAddon={<Search className="h-4 w-4" />}
          value={lookup}
          onChange={(e) => setLookup(e.target.value)}
          wrapperClassName="max-w-xl"
        />
        {lookup.trim().length >= 2 && (
          <ul className="mt-2 max-w-xl divide-y divide-slate-100 rounded border border-slate-200 text-sm" data-testid="quest-code-lookup">
            {searching && <li className="px-3 py-1.5 text-slate-500">Searching…</li>}
            {!searching && results.length === 0 && <li className="px-3 py-1.5 text-slate-500">No codes found</li>}
            {!searching &&
              results.map((r) => (
                <li key={r.testCode} className="flex items-center gap-2 px-3 py-1.5">
                  <span className="font-mono">{r.testCode}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-600">{r.description}</span>
                  <span className="text-xs text-slate-500">{[r.specimenType, r.state ? stateLabel(r.state) : ''].filter(Boolean).join(' · ')}</span>
                  {customCodes.has(r.testCode) && <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-600">Yours</span>}
                </li>
              ))}
          </ul>
        )}
      </div>

      {editing && (
        <QuestCodeModal
          key={editing === 'new' ? 'new' : editing.code}
          code={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
            onCreated();
          }}
          onSaved={() => {
            setEditing(null);
            onCreated();
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete lab code ${deleting.code}?` : ''}
        message="Staff can no longer order it. Orders already sent keep it."
        confirmLabel="Delete lab code"
        variant="danger"
      />
    </div>
  );
}

const SPECIMEN_TYPE_OPTIONS = [{ value: '', label: 'None' }, ...SPECIMEN_TYPES.map((s) => ({ value: s, label: s }))];

function QuestCodeModal({ code, onClose, onSaved }: { code: QuestCustomCode | null; onClose: () => void; onSaved: () => void }) {
  const isNew = code === null;
  // Locked: a viewer (AdminFormModal); nothing saves.
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(
    () => ({ code: code?.code ?? '', description: code?.description ?? '', specimenType: code?.specimenType ?? '', state: code?.state ?? '' }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const noCode = t.form.code.trim() === '';
  const spaceInCode = !noCode && !/^[A-Za-z0-9._-]+$/.test(t.form.code.trim());
  const noDescription = t.form.description.trim() === '';
  const errors = [
    ...(noCode ? ['Test Code is required'] : []),
    ...(spaceInCode ? ['Test Code can hold only letters, digits, ".", "-" and "_"'] : []),
    ...(noDescription ? ['Description is required'] : []),
  ];

  async function save() {
    if (errors.length > 0 || locked) return;
    setSaving(true);
    try {
      if (code) await updateQuestCode(code.code, { description: t.form.description, specimenType: t.form.specimenType, state: t.form.state });
      else await createQuestCode(t.form);
      toast.success(isNew ? 'Lab code created' : 'Lab code updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The lab code could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Lab Code' : `Edit Lab Code ${code.code}`}
      isNew={isNew}
      dirty={isNew ? errors.length === 0 : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <DialogField
        label="Test Code"
        htmlFor="quest-code-code"
        required={isNew}
        state={isNew && (noCode || spaceInCode) ? 'error' : 'none'}
        description={isNew || locked ? undefined : 'A code cannot be changed once added.'}
      >
        <Input
          id="quest-code-code"
          value={t.form.code}
          maxLength={QUEST_CODE_MAX}
          disabled={!isNew}
          onChange={(e) => t.patch({ code: e.target.value })}
          className={fieldClasses.input(isNew && (noCode || spaceInCode) ? 'error' : 'none')}
        />
      </DialogField>
      <DialogField label="Description" htmlFor="quest-code-description" required state={noDescription ? 'error' : 'none'} badge={t.badge('description', 'Description')}>
        <Input
          id="quest-code-description"
          value={t.form.description}
          maxLength={QUEST_DESCRIPTION_MAX}
          onChange={(e) => t.patch({ description: e.target.value })}
          className={fieldClasses.input(noDescription ? 'error' : 'none')}
        />
      </DialogField>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DialogField label="Specimen Type" htmlFor="quest-code-specimen" badge={t.badge('specimenType', 'Specimen Type')}>
          <Select<string> id="quest-code-specimen" options={SPECIMEN_TYPE_OPTIONS} value={t.form.specimenType} onChange={(v) => t.patch({ specimenType: v ?? '' })} searchable />
        </DialogField>
        <DialogField label="Specimen State" htmlFor="quest-code-state" badge={t.badge('state', 'Specimen State')}>
          <Select<string> id="quest-code-state" options={SPECIMEN_STATES} value={t.form.state} onChange={(v) => t.patch({ state: v ?? '' })} />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}
